/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Injectable, Signal, WritableSignal, computed, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, EMPTY, finalize, Observable, of, Subject, switchMap, takeUntil, tap, throwError} from 'rxjs';

import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {visiblePageNumbers} from '../directives/pagination/visible-page-numbers';
import {JOIN_INFO} from './join-data';
import {
  AdminObjectState,
  SignalAdminResourceApi,
  SignalAdminResourceListParams,
  SignalAdminResourceModel
} from './signal-admin-resource-api';

export interface SignalAdminListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

export interface SignalAdminResourceListOptions {
  listId: string;
  defaultOrderBy?: string;
  defaultAscending?: boolean;
  additionalParams?: Record<string, any>;
  /** API table name (object type apiParam); enables sorting on JOIN_INFO columns such as SiteGroup. */
  baseTable?: string;
  /** Child/embedded lists must not read or write the page's ?pageNo. Defaults to true. */
  syncPageToUrl?: boolean;
  /** Overrides the default `name;description;siteUrl;username` search fields. */
  searchFields?: string;
  /** Columns whose null values represent the oldest records. */
  nullsAsOldestParams?: string[];
  pageSize?: number;
  transformResults?: (items: any[]) => any[];
  /** Dynamic parameters for embedded editors and their existing filter controls. */
  requestParams?: () => Record<string, any>;
  afterLoad?: (items: any[]) => void;
  onError?: (error: any) => void;
  retainSelection?: boolean;
  saveSortSettings?: (settings: {orderByParam: string; asc: boolean; isNumeric: boolean}) => void;
}

export interface SignalAdminResourceListViewState<T extends SignalAdminResourceModel> {
  readonly readOnly: Signal<boolean>;
  readonly items: Signal<T[]>;
  readonly loading: Signal<boolean>;
  readonly searching: Signal<boolean>;
  readonly sorting: Signal<boolean>;
  readonly paging: Signal<boolean>;
  readonly bulkSaving: Signal<boolean>;
  readonly totalCount: Signal<number>;
  readonly currentPage: Signal<number>;
  readonly include: Signal<AdminObjectState>;
  readonly orderBy: Signal<string>;
  readonly ascending: Signal<boolean>;
  readonly selected: Signal<Record<string, T>>;
  readonly undoItems: Signal<Array<{id: string; objectState: AdminObjectState}>>;
  readonly undoAction: Signal<AdminObjectState | null>;
  readonly undoBulkSelection: Signal<Partial<T>[]>;
  readonly bulkUndoError: Signal<boolean>;
  readonly loadFailed: Signal<boolean>;
  readonly searchTerm: WritableSignal<string>;
  readonly committedSearch: Signal<string>;
  readonly selectedCount: Signal<number>;
  readonly allSelected: Signal<boolean>;
  readonly partiallySelected: Signal<boolean>;
  readonly totalPages: Signal<number>;
  readonly pageNumbers: Signal<number[]>;
  readonly pageRangeStart: Signal<number>;
  readonly pageRangeEnd: Signal<number>;
  readonly pageSize: number;
  readonly hideHidden: boolean;

  load(): void;
  commitSearch(clear?: boolean): void;
  columnLabel(column: SignalAdminListColumn): string;
  sort(column: SignalAdminListColumn, event: Event): void;
  showObjectState(objectState: AdminObjectState): void;
  changePage(page: number, event: Event): void;
  toggle(item: T): void;
  selectAll(): void;
  deselectAll(): void;
  setSelectedObjectState(objectState: AdminObjectState): void;
  undoObjectStateChange(): void;
  saveMiscBulkAction(items: Partial<T>[]): Observable<void>;
  undoMiscBulkAction(): void;
}

export class SignalAdminResourceListState<T extends SignalAdminResourceModel>
implements SignalAdminResourceListViewState<T> {
  readonly readOnly = signal(false);
  readonly items = signal<T[]>([]);
  readonly loading = signal(true);
  readonly searching = this.loading;
  readonly sorting = this.loading;
  readonly paging = this.loading;
  readonly bulkSaving = signal(false);
  readonly totalCount = signal(0);
  readonly currentPage = signal(1);
  readonly include = signal<AdminObjectState>('ACTIVE');
  readonly orderBy = signal('name');
  readonly ascending = signal(true);
  readonly nullsOrderPolicy = signal<'FIRST' | 'LAST' | undefined>(undefined);
  private numericSort = false;
  readonly selected = signal<Record<string, T>>({});
  readonly undoItems = signal<Array<{id: string; objectState: AdminObjectState}>>([]);
  readonly undoAction = signal<AdminObjectState | null>(null);
  readonly undoBulkSelection = signal<Partial<T>[]>([]);
  readonly bulkUndoError = signal(false);
  readonly loadFailed = signal(false);
  readonly searchTerm = signal('');
  readonly committedSearch = signal('');
  readonly additionalParams: WritableSignal<Record<string, any>>;
  readonly selectIn: Record<string, any> = {};
  userStateWorking: string[] = [];
  executionStateWorking: string[] = [];
  sourceWorking: string[] = [];
  joinFilterBy = '';
  savedFilterBy: string | null = null;
  joinMessage = '';
  showOrphan = false;
  joinLayout: any;
  joinLineItem: any;
  joinCreativeSet: any;
  joinAdvertiser: any;
  joinTeam: any;
  joinSalesperson: any;
  joinAdOps: any;

  private readonly parameterGroups: Record<string, Record<string, any>> = {};

  readonly pageSize: number;
  readonly hideHidden: boolean;
  readonly selectedCount = computed(() => Object.keys(this.selected()).length);
  readonly allSelected = computed(() => this.items().length > 0
    && this.items().every((item) => !!this.selected()[item.id]));
  readonly partiallySelected = computed(() => this.selectedCount() > 0 && !this.allSelected());
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize)));
  readonly pageNumbers = computed(() => visiblePageNumbers(this.currentPage(), this.totalPages()));
  readonly pageRangeStart = computed(() => this.pageSize * (this.currentPage() - 1) + 1);
  readonly pageRangeEnd = computed(() => Math.min(this.pageSize * this.currentPage(), this.totalCount()));

  private readonly reload = new Subject<void>();
  private readonly disposed = new Subject<void>();

  constructor(
    private readonly api: SignalAdminResourceApi<T>,
    private readonly options: SignalAdminResourceListOptions,
    private readonly localUserProfile: LocalUserProfile,
    localNetworkProfile: LocalNetworkProfile,
    private readonly location: BrowserLocationService,
    private readonly destroyRef: DestroyRef
  ) {
    const profile = this.localUserProfile.getProfile() || {};
    const settings = profile.adnLists?.[options.listId] || {};
    this.pageSize = Math.max(1, Math.min(100, options.pageSize || profile.adnPrefs?.defaultPageSize || 25));
    this.hideHidden = localNetworkProfile.isPureMarketplacePlatform();
    this.additionalParams = signal({...options.additionalParams});
    this.orderBy.set(settings.orderByParam || options.defaultOrderBy || 'name');
    this.ascending.set(settings.asc === undefined ? options.defaultAscending !== false : !!settings.asc);
    this.numericSort = !!settings.isNumeric;
    const page = options.syncPageToUrl === false ? 1 : Number(this.location.queryParams.pageNo);
    this.currentPage.set(Number.isFinite(page) && page > 0 ? page : 1);

    this.reload.pipe(
      switchMap(() => {
        this.loading.set(true);
        this.loadFailed.set(false);
        return this.loadAvailablePage().pipe(
          catchError((error) => {
            this.loadFailed.set(true);
            options.onError?.(error);
            return EMPTY;
          }),
          finalize(() => this.loading.set(false))
        );
      }),
      takeUntil(this.disposed),
      takeUntilDestroyed(destroyRef)
    ).subscribe((pageResult) => {
      const results = pageResult.results || [];
      this.items.set(options.transformResults ? options.transformResults(results) : results);
      this.totalCount.set(pageResult.totalCount || 0);
      if (!options.retainSelection) this.selected.set({});
      options.afterLoad?.(this.items());
    });
  }

  private loadAvailablePage(): Observable<{results: T[]; totalCount: number}> {
    const requestedPage = this.currentPage();
    return this.api.list(this.listParams()).pipe(
      switchMap((pageResult) => {
        const totalCount = pageResult.totalCount || 0;
        const lastPage = Math.max(1, Math.ceil(totalCount / this.pageSize));
        if (requestedPage > lastPage) {
          this.currentPage.set(lastPage);
          if (this.options.syncPageToUrl !== false) {
            this.location.setQueryParam('pageNo', lastPage === 1 ? undefined : lastPage);
          }
          return this.loadAvailablePage();
        }
        return of(pageResult);
      })
    );
  }

  destroy(): void {
    this.disposed.next();
    this.disposed.complete();
    this.reload.complete();
  }

  load(): void {
    this.reload.next();
  }

  commitSearch(clear = false): void {
    if (this.searching()) {
      return;
    }
    if (clear) {
      this.searchTerm.set('');
    }
    this.committedSearch.set(this.searchTerm().trim());
    this.currentPage.set(1);
    this.load();
  }

  setAdditionalParams(params: Record<string, any>): void {
    this.additionalParams.set({...params});
    this.currentPage.set(1);
    this.load();
  }

  makeSelectInQuery(field: string, values: string[] | null, maxCount?: number): void {
    const selectedValues = values && (!maxCount || values.length < maxCount) ? values : [];
    this.selectIn[field] = Object.fromEntries(selectedValues.map((value) => [value, true]));
    this.selectIn[field + 'Selected'] = selectedValues.length > 0;
    (this as any)[field + 'Working'] = [...selectedValues];
    this.currentPage.set(1);
    this.load();
  }

  setFilterParams(group: string, params: Record<string, any>): void {
    this.parameterGroups[group] = {...params};
    this.currentPage.set(1);
    this.load();
  }

  filterOnSelect(baseTable: string, clearOut = false): void {
    const filterBy = clearOut ? null : this.joinFilterBy;
    const joinInfo = filterBy ? JOIN_INFO[filterBy] : null;
    if (this.showOrphan && !clearOut) {
      this.setFilterParams('join', {
        where: `${baseTable}.lineItemId is null;${baseTable}.creativeSetId is null`
      });
      this.joinMessage = `${joinInfo?.translation || ''}: orphan`;
    } else if (joinInfo) {
      const selected = (this as any)['join' + filterBy];
      const name = selected?.name || '';
      this.setFilterParams('join', name ? {
        joins: `inner join ${joinInfo.table} on ${baseTable}.${joinInfo.field}=${joinInfo.joinField}`,
        where: `${joinInfo.whereField}=${name}`
      } : {});
      this.joinMessage = name ? `${joinInfo.translation}: ${name}` : '';
      this.showOrphan = false;
    } else {
      this.setFilterParams('join', {});
      this.joinMessage = '';
      this.showOrphan = false;
    }
    this.savedFilterBy = filterBy;
  }

  columnLabel(column: SignalAdminListColumn): string {
    return 'label.' + (column.label || column.param);
  }

  sort(column: SignalAdminListColumn, event: Event): void {
    event.preventDefault();
    if (this.sorting() || column.sortable === false || !column.param) {
      return;
    }
    const nextAscending = this.orderBy() === column.param ? !this.ascending() : !column.numeric;
    this.numericSort = !!column.numeric;
    this.nullsOrderPolicy.set(this.numericSort || this.options.nullsAsOldestParams?.includes(column.param)
      ? nextAscending ? 'FIRST' : 'LAST' : undefined);
    this.orderBy.set(column.param);
    this.ascending.set(nextAscending);
    this.currentPage.set(1);
    this.saveListSettings();
    this.load();
  }

  showObjectState(objectState: AdminObjectState): void {
    if (this.include() === objectState) {
      return;
    }
    this.include.set(objectState);
    this.currentPage.set(1);
    this.undoItems.set([]);
    this.load();
  }

  changePage(page: number, event: Event): void {
    event.preventDefault();
    if (this.paging() || page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }
    this.currentPage.set(page);
    if (this.options.syncPageToUrl !== false) {
      this.location.setQueryParam('pageNo', page === 1 ? undefined : page);
    }
    this.load();
  }

  toggle(item: T): void {
    this.selected.update((selection) => {
      const next = {...selection};
      if (next[item.id]) {
        delete next[item.id];
      } else {
        next[item.id] = item;
      }
      return next;
    });
  }

  selectAll(): void {
    this.selected.set(Object.fromEntries(this.items().map((item) => [item.id, item])));
  }

  deselectAll(): void {
    this.selected.set({});
  }

  setSelectedObjectState(objectState: AdminObjectState): void {
    if (this.readOnly() || this.bulkSaving() || this.selectedCount() === 0) {
      return;
    }
    const selectedItems = Object.values(this.selected());
    const undoItems = selectedItems.map((item) => ({
      id: item.id,
      objectState: item.objectState || this.include()
    }));
    this.saveObjectStates(
      selectedItems.map((item) => ({id: item.id, objectState})),
      () => {
        this.undoItems.set(undoItems);
        this.undoAction.set(objectState);
      }
    );
  }

  undoObjectStateChange(): void {
    if (this.readOnly() || !this.undoItems().length || this.bulkSaving()) {
      return;
    }
    this.saveObjectStates(this.undoItems(), () => {
      this.undoItems.set([]);
      this.undoAction.set(null);
    });
  }

  saveMiscBulkAction(items: Partial<T>[]): Observable<void> {
    if (this.readOnly()) return throwError(() => new Error('Read-only is the sole option'));
    if (!items.length) {
      return throwError(() => new Error('A bulk edit requires at least one item.'));
    }
    const selected = this.selected();
    const undoItems: Partial<T>[] = [];
    for (const item of items) {
      if (!item.id || !selected[item.id]) {
        return throwError(() => new Error('A bulk edit item is not part of the current selection.'));
      }
      const original = selected[item.id];
      undoItems.push(Object.fromEntries(
        Object.keys(item).map((key) => [key, (original as any)[key]])
      ) as Partial<T>);
    }
    this.bulkUndoError.set(false);
    this.bulkSaving.set(true);
    return this.api.saveMany(items).pipe(
      tap(() => {
        this.undoBulkSelection.set(undoItems);
        this.selected.set({});
        this.load();
      }),
      finalize(() => this.bulkSaving.set(false)),
      takeUntil(this.disposed),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  undoMiscBulkAction(): void {
    const items = this.undoBulkSelection();
    if (this.readOnly() || !items.length || this.bulkSaving()) {
      return;
    }
    this.bulkUndoError.set(false);
    this.bulkSaving.set(true);
    this.api.saveMany(items).pipe(
      finalize(() => this.bulkSaving.set(false)),
      takeUntil(this.disposed),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.undoBulkSelection.set([]);
        this.load();
      },
      error: () => this.bulkUndoError.set(true)
    });
  }

  private saveObjectStates(
    items: Array<{id: string; objectState: AdminObjectState}>,
    onSuccess: () => void
  ): void {
    this.bulkSaving.set(true);
    this.api.setObjectStates(items).pipe(
      finalize(() => this.bulkSaving.set(false)),
      takeUntil(this.disposed),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        onSuccess();
        this.load();
      },
      error: () => undefined
    });
  }

  private listParams(): SignalAdminResourceListParams {
    const include = this.include();
    const join = this.options.baseTable ? JOIN_INFO[this.orderBy()] : null;
    const nullsAsOldest = this.numericSort || this.options.nullsAsOldestParams?.includes(this.orderBy());
    const selectWhere = Object.entries(this.selectIn)
      .filter(([key, value]) => !key.endsWith('Selected') && value && typeof value === 'object')
      .flatMap(([field, value]) => {
        const values = Object.keys(value).filter((key) => value[key] === true);
        return values.length ? [`${this.options.baseTable || ''}.${field} in ${values.join(',')}`] : [];
      });
    const additionalParams = Object.assign({}, this.additionalParams(), ...Object.values(this.parameterGroups));
    const groupedWhere = Object.values(this.parameterGroups).map((params) => params.where).filter(Boolean);
    const groupedJoins = Object.values(this.parameterGroups).map((params) => params.joins).filter(Boolean);
    const where = [this.additionalParams().where, ...groupedWhere, ...selectWhere].filter(Boolean).join(';');
    const joins = [this.additionalParams().joins, ...groupedJoins].filter(Boolean).join(';');
    return {
      includeActive: include === 'ACTIVE',
      includeInactive: include === 'INACTIVE',
      includeHidden: include === 'HIDDEN',
      orderBy: join ? join.whereField : this.orderBy(),
      ...(join ? {joins: [`left join ${join.table} on ${this.options.baseTable}.${join.field}=${join.joinField}`]} : {}),
      orderByDirection: this.ascending() ? 'ASCENDING' : 'DESCENDING',
      ...(nullsAsOldest ? {nullsOrderPolicy: this.ascending() ? 'FIRST' as const : 'LAST' as const} : {}),
      pageSize: this.pageSize,
      page: this.currentPage(),
      concise: true,
      locale: this.localUserProfile.get('locale'),
      ...(this.committedSearch() ? {
        filterBy: this.options.searchFields || 'name;description;siteUrl;username',
        filterByLike: this.committedSearch()
      } : {}),
      ...additionalParams,
      ...(where ? {where} : {}),
      ...(joins ? {joins} : {}),
      ...this.options.requestParams?.()
    };
  }

  private saveListSettings(): void {
    if (this.options.saveSortSettings) {
      this.options.saveSortSettings({orderByParam: this.orderBy(), asc: this.ascending(), isNumeric: this.numericSort});
      return;
    }
    this.localUserProfile.set(`profile.adnLists.${this.options.listId}.orderByParam`, this.orderBy());
    this.localUserProfile.set(`profile.adnLists.${this.options.listId}.asc`, this.ascending());
    this.localUserProfile.set(`profile.adnLists.${this.options.listId}.isNumeric`, this.numericSort);
  }
}

@Injectable({providedIn: 'root'})
export class SignalAdminResourceListStateFactory {
  constructor(
    private readonly localUserProfile: LocalUserProfile,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly location: BrowserLocationService
  ) {
  }

  create<T extends SignalAdminResourceModel>(
    api: SignalAdminResourceApi<T>,
    options: SignalAdminResourceListOptions,
    destroyRef: DestroyRef
  ): SignalAdminResourceListState<T> {
    return new SignalAdminResourceListState(
      api,
      options,
      this.localUserProfile,
      this.localNetworkProfile,
      this.location,
      destroyRef
    );
  }
}
